import { Routes } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { AuthGuard } from '@core/guards/auth.guard';
import { notFoundGuard } from '@core/guards/not-found.guard';
import { Roles } from '@core/constants/roles.constants';

const DEFAULT_ROUTE = '/home';

export const routes: Routes = [
  { path: '', redirectTo: DEFAULT_ROUTE, pathMatch: 'full' },
  {
    path: 'home',
    loadComponent: () =>
      import('@features/home/home.component').then((m) => m.HomeComponent),
    canActivate: [MsalGuard],
  },
  {
    path: 'admin',
    loadChildren: () =>
      import('./features/admin/admin.routes').then((m) => m.ADMIN_ROUTES),
    canActivate: [AuthGuard],
    data: { roles: [Roles.Admin] },
  },
  {
    path: 'ressources', // /ressources
    canActivate: [MsalGuard],
    children: [
      {
        path: 'formateurs', // /ressources/formateurs
        loadComponent: () =>
          import(
            '@features/ressources/components/formateurs/formateurs.component'
          ).then((m) => m.FormateursComponent),
        canActivate: [MsalGuard],
      },
      {
        path: 'apprenants',
        canActivate: [MsalGuard],
        children: [
          {
            path: '', // /apprenants
            loadComponent: () =>
              import(
                '@features/ressources/components/apprenants/apprenants.component'
              ).then((m) => m.ApprenantsComponent),
            canActivate: [MsalGuard],
          },
          {
            path: ':id', // /apprenants/:id
            children: [
              {
                path: '', // /apprenants/:id
                loadComponent: () =>
                  import(
                    '@features/ressources/components/apprenants/components/apprenant-manager/apprenant-manager.component'
                  ).then((m) => m.ApprenantManagerComponent),
                canActivate: [MsalGuard],
              },
            ],
          },
        ],
      },
      {
        path: 'personnesMorales', // /ressources/formateurs
        loadComponent: () =>
          import(
            '@features/ressources/components/personnes-morales/personnes-morales.component'
          ).then((m) => m.PersonnesMoralesComponent),
        canActivate: [MsalGuard],
      },
      {
        path: 'personnesPhysique', // /ressources/formateurs
        loadComponent: () =>
          import(
            '@features/ressources/components/personnes-physiques/personnes-physiques.component'
          ).then((m) => m.PersonnesPhysiquesComponent),
        canActivate: [MsalGuard],
      },
    ],
  },
  {
    path: 'inscription',
    canActivate: [MsalGuard],
    children: [
      {
        path: '', // /inscriptions
        loadComponent: () =>
          import('@features/inscriptions/inscriptions.component').then(
            (m) => m.InscriptionsComponent,
          ),
        canActivate: [MsalGuard],
      },
      {
        path: ':id', // /inscriptions/:id
        children: [
          {
            path: '', // /inscriptions/:id
            loadComponent: () =>
              import(
                '@features/inscriptions/inscription-manager/inscription-manager.component'
              ).then((m) => m.InscriptionManagerComponent),
            canActivate: [MsalGuard],
          },
        ],
      },
    ],
  },
  {
    path: 'inscriptions',
    canActivate: [MsalGuard],
    children: [
      {
        path: '', // /inscriptions
        loadComponent: () =>
          import('@features/inscriptions/inscriptions.component').then(
            (m) => m.InscriptionsComponent,
          ),
        canActivate: [MsalGuard],
      },
      {
        path: 'apprenant', // /inscriptions/apprenant
        children: [
          {
            path: ':id', // /inscriptions/apprenant/:id
            loadComponent: () =>
              import('@features/inscriptions/inscriptions.component').then(
                (m) => m.InscriptionsComponent,
              ),
            canActivate: [MsalGuard],
          },
        ],
      },
    ],
  },
  {
    path: 'formations',
    canActivate: [MsalGuard],
    children: [
      {
        path: '', // /formations
        loadComponent: () =>
          import('@features/formations/formations.component').then(
            (m) => m.FormationsComponent,
          ),
        canActivate: [MsalGuard],
      },
      {
        path: 'new', // /formations/new
        loadComponent: () =>
          import(
            '@features/formations/components/formation-manager/formation-manager.component'
          ).then((m) => m.FormationManagerComponent),
        canActivate: [MsalGuard],
      },
      {
        path: ':id', // /formations/:id
        children: [
          {
            path: '', // /formations/:id
            loadComponent: () =>
              import(
                '@features/formations/components/formation-manager/formation-manager.component'
              ).then((m) => m.FormationManagerComponent),
            canActivate: [MsalGuard],
          },
          {
            path: 'modules', // /formations/:id/modules
            loadComponent: () =>
              import(
                '@features/formations/components/module-manager/module-manager.component'
              ).then((m) => m.ModuleManagerComponent),
            canActivate: [MsalGuard],
          },
        ],
      },
    ],
  },
  {
    path: 'sessions',
    canActivate: [MsalGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@features/sessions/sessions.component').then(
            (m) => m.SessionsComponent,
          ),
        canActivate: [MsalGuard],
      },
      {
        path: 'formation/:formationId', // /sessions/formation/:formationId
        loadComponent: () =>
          import('@features/sessions/sessions.component').then(
            (m) => m.SessionsComponent,
          ),
        canActivate: [MsalGuard],
      },
      {
        path: 'new', // /sessions/new
        loadComponent: () =>
          import(
            '@features/sessions/components/session-manager/session-manager.component'
          ).then((m) => m.SessionManagerComponent),
        canActivate: [MsalGuard],
      },
      {
        path: ':id', // /sessions/:id
        children: [
          {
            path: '', // /sessions/:id
            loadComponent: () =>
              import(
                '@features/sessions/components/session-manager/session-manager.component'
              ).then((m) => m.SessionManagerComponent),
            canActivate: [MsalGuard],
          },
          {
            path: 'cours', // /sessions/:id/cours
            loadComponent: () =>
              import('@features/sessions/cours.component').then(
                (m) => m.CoursComponent,
              ),
            canActivate: [MsalGuard],
          },
          {
            path: 'cours/new', // /sessions/:id/cours/new
            loadComponent: () =>
              import(
                '@features/sessions/components/cours-manager/cours-manager.component'
              ).then((m) => m.CoursManagerComponent),
            canActivate: [MsalGuard],
          },
          {
            path: 'cours/:coursId', // /sessions/:id/cours/:coursId
            data: {
              isEditMode: true,
            },
            loadComponent: () =>
              import(
                '@features/sessions/components/cours-manager/cours-manager.component'
              ).then((m) => m.CoursManagerComponent),
            canActivate: [MsalGuard],
          },
          {
            path: 'planning', // /sessions/:id/planning
            loadComponent: () =>
              import(
                '@features/sessions/components/session-planning/session-planning.component'
              ).then((m) => m.PlanningSessionComponent),
            canActivate: [MsalGuard],
          },
          {
            path: 'apprenants', // /sessions/:id/apprenants
            loadComponent: () =>
              import(
                '@app/features/sessions/components/apprenant-list/apprenant-session-list.component'
              ).then((m) => m.ApprenantSessionListComponent),
            canActivate: [MsalGuard],
          },
          {
            path: 'apprenants/exportExcelTemplate', // /sessions/:id/apprenants/exportExcelTemplate
            loadComponent: () =>
              import(
                '@app/features/sessions/components/apprenant-list/export-excel-template/export-excel-template.component'
              ).then((m) => m.ExportExcelTemplateComponent),
            canActivate: [MsalGuard],
          },
          {
            path: 'apprenants/addApprenants', // /sessions/:id/apprenants/addApprenants
            loadComponent: () =>
              import(
                '@app/features/sessions/components/apprenant-list/add-apprenants/add-apprenants.component'
              ).then((m) => m.AddApprenantsComponent),
            canActivate: [MsalGuard],
          },
          {
            path: 'apprenants/importMassif', // /sessions/:id/apprenants/importMassif
            loadComponent: () =>
              import(
                '@app/features/sessions/components/apprenant-list/import-massif/import-massif.component'
              ).then((m) => m.ImportMassifComponent),
            canActivate: [MsalGuard],
          },
        ],
      },
    ],
  },
  {
    path: 'learners',
    loadComponent: () =>
      import('@features/learners/learners.component').then(
        (m) => m.LearnersComponent,
      ),
    canActivate: [MsalGuard],
  },
  {
    path: '**',
    loadComponent: () =>
      import('@core/components/not-found/not-found.component'),
    canActivate: [notFoundGuard],
  },
];
